import { useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/lib/hooks';
import { Button } from './Primitives';
import { countdown } from '@/lib/format';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** One sentence of what happens. Never "are you sure". */
  body?: string;
  /** Concrete side effects, listed. */
  effects: readonly string[];
  confirmLabel: string;
  onConfirm: (payload: { reason: string | null }) => void | Promise<void>;
  onCancel: () => void;
  /** Tier 3 variant: the operator types this exact string before the button enables. */
  typedConfirm?: string;
  reasonRequired?: boolean;
  /** Tier 3 cooldown. The button stays disabled until it passes. */
  cooldownUntil?: string | null;
  destructive?: boolean;
  /** Shown under the actions: the audit and Slack consequences. */
  note?: string;
  /**
   * Controlled reason. Pass both to own the value, for example when the caller needs the
   * reason for a preview call before the operator confirms. Omit both and the dialog keeps
   * its own state and hands the reason to onConfirm.
   */
  reason?: string;
  onReasonChange?: (reason: string) => void;
  /** The caller is already running the command. Keeps the button spinning across a re-render. */
  busy?: boolean;
  /** Extra content between the effects and the reason, for example a diff or a preview. */
  extra?: ReactNode;
}

/**
 * The dialog every irreversible action goes through. It lists what will happen, takes the
 * reason the audit row needs, and for a tier 3 control makes the operator type the exact
 * string. There is no Undo behind this: mark paid, refunds, cancels and production toggles
 * get a confirmation and an audit row instead.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  effects,
  confirmLabel,
  onConfirm,
  onCancel,
  typedConfirm,
  reasonRequired = false,
  cooldownUntil,
  destructive = false,
  note,
  reason: controlledReason,
  onReasonChange,
  busy: externalBusy = false,
  extra,
}: ConfirmDialogProps) {
  const id = useId();
  const [typed, setTyped] = useState('');
  const [ownReason, setOwnReason] = useState('');
  const [ownBusy, setOwnBusy] = useState(false);

  const controlled = controlledReason !== undefined;
  const reason = controlled ? controlledReason : ownReason;
  const setReason = (value: string) => {
    if (!controlled) setOwnReason(value);
    onReasonChange?.(value);
  };
  const busy = ownBusy || externalBusy;
  const [failure, setFailure] = useState<string | null>(null);
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) {
      setTyped('');
      setOwnReason('');
      setOwnBusy(false);
      setFailure(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel, open]);

  if (!open || typeof document === 'undefined') return null;

  const cooling = Boolean(cooldownUntil) && Date.parse(cooldownUntil ?? '') > Date.now();
  const typedOk = typedConfirm === undefined || typed === typedConfirm;
  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const canConfirm = typedOk && reasonOk && !busy && !cooling;

  const submit = async () => {
    if (!canConfirm) return;
    setOwnBusy(true);
    setFailure(null);
    try {
      await onConfirm({ reason: reasonRequired ? reason.trim() : (reason.trim() || null) });
    } catch (error) {
      // The form stays filled so the operator can retry without retyping.
      setFailure(error instanceof Error ? error.message : 'The action failed.');
    } finally {
      setOwnBusy(false);
    }
  };

  return createPortal(
    <>
      <button
        type="button"
        className="o-scrim o-scrim--dialog"
        aria-label="Cancel"
        tabIndex={-1}
        onClick={busy ? undefined : onCancel}
      />
      <div
        className="o-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        ref={(node) => {
          trapRef.current = node;
        }}
      >
        <h2 className="o-dialog__title" id={`${id}-title`}>
          {title}
        </h2>
        {body ? <p className="o-dialog__body">{body}</p> : null}

        {effects.length > 0 ? (
          <ul className="o-dialog__effects">
            {effects.map((effect) => (
              <li key={effect}>{effect}</li>
            ))}
          </ul>
        ) : null}

        {reasonRequired || typedConfirm !== undefined ? (
          <div className="o-field">
            <label className="o-field__label" htmlFor={`${id}-reason`}>
              Reason {reasonRequired ? '(required)' : '(optional)'}
            </label>
            <textarea
              id={`${id}-reason`}
              className="o-textarea"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required={reasonRequired}
            />
          </div>
        ) : null}

        {typedConfirm !== undefined ? (
          <div className="o-field">
            <label className="o-field__label" htmlFor={`${id}-typed`}>
              Type {typedConfirm} to confirm
            </label>
            <input
              id={`${id}-typed`}
              className="o-input o-input--mono"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
              aria-describedby={`${id}-typed-hint`}
            />
            <span className="o-field__hint" id={`${id}-typed-hint`}>
              Exact match, including case.
            </span>
          </div>
        ) : null}

        {extra}

        {cooling ? (
          <p className="o-field__error">
            Cooldown active. Available in {countdown(cooldownUntil ?? null)}.
          </p>
        ) : null}
        {failure ? <p className="o-field__error">{failure}</p> : null}

        <div className="o-dialog__actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={!canConfirm}
            busy={busy}
          >
            {confirmLabel}
          </Button>
        </div>

        {note ? <p className="o-dialog__note">{note}</p> : null}
      </div>
    </>,
    document.body,
  );
}
